import React, { useEffect, useState, useCallback } from "react";
import { api, fmtNum, curSym, fmtDate, errMsg } from "@finance/lib/api";
import { useAuth } from "@finance/context/AuthContext";
import Layout, { PageHeader } from "@finance/components/Layout";
import { Button } from "@finance/components/ui/button";
import { toast } from "sonner";
import { CheckCircle, Trash, CalendarBlank, Archive } from "@phosphor-icons/react";
import { KpiStrip, KpiCard, EmptyState } from "@finance/components/DesignSystem";

const TABS = [
  { key: "scheduled", label: "Payment Schedule" },
  { key: "archived", label: "Archived Payments" },
];

export default function PaymentSchedule() {
  const { user } = useAuth();
  const canManage = user?.is_owner || user?.can_manage_payment_schedule;
  const canAuthorize = user?.is_owner || user?.can_authorize_payments;
  const [tab, setTab] = useState("scheduled");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    api.get("/payment-schedule", { params: { status: tab } }).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const authorize = async (s) => {
    if (!window.confirm(`Authorize payment of ${curSym(s.currency)} ${fmtNum(s.amount)} to ${s.supplier_name || "supplier"}? This posts the payment to the GL.`)) return;
    setBusy(s.id);
    try {
      await api.post(`/payment-schedule/${s.id}/authorize`);
      toast.success("Payment authorized & posted");
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(null); }
  };

  const remove = async (s) => {
    if (!window.confirm("Remove this item from the payment schedule?")) return;
    setBusy(s.id);
    try {
      await api.delete(`/payment-schedule/${s.id}`);
      toast.success("Removed from schedule");
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(null); }
  };

  const total = rows.reduce((s, r) => s + (r.amount || 0), 0);
  const scheduled = tab === "scheduled";

  return (
    <Layout>
      <PageHeader
        title="Payment Schedule"
        subtitle="Assign a paying bank, then the owner authorizes — posting the payment to the ledger"
      />
      <div className="p-8">
        <KpiStrip cols={2}>
          <KpiCard icon={scheduled ? CalendarBlank : Archive} label={scheduled ? "Awaiting authorization" : "Archived"} value={rows.length} testid="kpi-sched-count" />
          <KpiCard icon={CheckCircle} label="Total" value={fmtNum(total, "TZS")} tone="accent" testid="kpi-sched-total" />
        </KpiStrip>

        <div className="mb-4 flex gap-2">
          {TABS.map((t) => (
            <button key={t.key} data-testid={`sched-tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`rounded-sm px-3 py-1.5 text-sm transition-colors ${tab === t.key ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
              {t.label}
            </button>
          ))}
        </div>

        {rows.length === 0 ? (
          <EmptyState icon={scheduled ? CalendarBlank : Archive} testid="sched-empty"
            title={scheduled ? "Nothing scheduled" : "No archived payments"}
            message={scheduled ? "Push a payable into the schedule from Payables Due to assign a bank and FX rate." : "Authorized payments will be archived here."} />
        ) : (
          <div className="overflow-x-auto rounded-sm border border-gray-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-gray-50"><tr className="lx-label border-b border-gray-200">
                <th className="px-4 py-2 text-left">Supplier</th>
                <th className="px-4 py-2 text-left">Reference</th>
                <th className="px-4 py-2 text-right">Amount</th>
                <th className="px-4 py-2 text-left">Bank</th>
                <th className="px-4 py-2 text-left">{scheduled ? "Assigned" : "Authorized"}</th>
                <th className="px-4 py-2 text-right">{scheduled ? "Actions" : "Payment"}</th>
              </tr></thead>
              <tbody>
                {rows.map((s) => (
                  <tr key={s.id} data-testid={`sched-row-${s.id}`} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-2 font-medium">{s.supplier_name || "—"}
                      <div className="text-xs text-gray-400">{s.lx_po_no || s.ref || ""}{s.note ? ` · ${s.note}` : ""}</div>
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-600">{s.source === "expense" ? "Expense" : "Supplier Invoice"}</td>
                    <td className="px-4 py-2 text-right lx-num">{curSym(s.currency)} {fmtNum(s.amount)}
                      {s.bank_currency && s.bank_currency !== s.currency && (
                        <div className="text-[11px] font-normal text-gray-400">@ {fmtNum(s.fx_rate)} → {s.bank_currency}</div>
                      )}
                    </td>
                    <td className="px-4 py-2 text-xs">{s.bank_name || "—"}</td>
                    <td className="px-4 py-2 text-xs text-gray-500">
                      {scheduled ? `${s.assigned_by || ""} · ${fmtDate(s.assigned_at)}` : `${s.authorized_by || ""} · ${fmtDate(s.authorized_at)}`}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {scheduled ? (
                        <div className="flex justify-end gap-1.5">
                          {canAuthorize && (
                            <button className="inline-flex items-center gap-1 text-xs text-emerald-600 hover:underline disabled:opacity-40" disabled={busy === s.id} data-testid={`authorize-${s.id}`} onClick={() => authorize(s)}><CheckCircle size={14} />Authorize</button>
                          )}
                          {canManage && (
                            <button className="inline-flex items-center gap-1 text-xs text-red-500 hover:underline disabled:opacity-40" disabled={busy === s.id} data-testid={`remove-sched-${s.id}`} onClick={() => remove(s)}><Trash size={14} />Remove</button>
                          )}
                          {!canAuthorize && !canManage && <span className="text-xs text-gray-400">—</span>}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">{(s.payment_id || "").slice(0, 8) || "—"}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
